/**
 * Display ordering for vendor auth templates and vendor endpoints.
 *
 * 1. `order` ascending (values need not be sequential).
 * 2. Items without a numeric `order` (null/missing) go after all ordered items.
 * 3. Ties (same `order`, or both unordered) break by `display_name`, case-insensitive.
 *
 * Never orders by id. The SDK and embedded UI implement the same rule, so the tie-break
 * is a plain lowercase code-unit comparison rather than a locale-aware one.
 */
export type DisplayOrderable = {
  order?: number | null;
  display_name?: string | null;
};

const hasOrder = (item: DisplayOrderable): item is { order: number } =>
  typeof item?.order === 'number' && Number.isFinite(item.order);

export function compareByDisplayOrder(a: DisplayOrderable, b: DisplayOrderable): number {
  const aOrdered = hasOrder(a);
  const bOrdered = hasOrder(b);

  if (aOrdered !== bOrdered) {
    return aOrdered ? -1 : 1;
  }
  if (aOrdered && bOrdered && a.order !== b.order) {
    return a.order - b.order;
  }

  const aName = (a?.display_name ?? '').toLowerCase();
  const bName = (b?.display_name ?? '').toLowerCase();
  if (aName < bName) return -1;
  if (aName > bName) return 1;
  return 0;
}

/** Returns a new array sorted by display order; never mutates the input. */
export function sortByDisplayOrder<T extends DisplayOrderable>(items?: T[] | null): T[] {
  return [...(items ?? [])].sort(compareByDisplayOrder);
}

/**
 * Selection to use for a list already in display order: keep `savedId` if it's still one of
 * the items (editing keeps what was saved), otherwise default to the first item. Ids are
 * compared as strings since form values may hold a stringified id.
 */
export function getDefaultSelectionId<T extends { id?: number | string }>(
  sortedItems: T[],
  savedId?: number | string | null,
): T['id'] | undefined {
  const saved =
    savedId == null ? undefined : sortedItems.find((item) => String(item.id) === String(savedId));
  return (saved ?? sortedItems[0])?.id;
}
